/**
 * The day a cost counts on — TRBUD-458.
 *
 * A cost counts in reports on the day the work, service or part use happened,
 * not on the day somebody uploaded, posted or closed something. This module
 * holds the words for where that day came from, the body of the audited
 * correction of a POSTED cost's day, and the pure rule that shares one visit's
 * rounded labour minutes across fleet-calendar days.
 *
 * Browser-safe: zod, types and labels.
 */
import { z } from "zod";
import { boundedDateString } from "./field-shapes.js";
import { idempotencyKey, strictObject } from "./requests.js";

/**
 * Where a cost's reporting day came from. The same words `tb_cost_report_day`
 * and `v_cost_line_spend.cost_date_source` answer with (migration
 * 20261030133015), in the order the database tries them:
 *
 * - `corrected`: a person corrected the day after posting (newest correction wins).
 * - `service`: the reviewed service day from a receipt or a typed service day.
 * - `used`: the day a stocked part was used on the job.
 * - `unknown`: recorded as not known; it counts in no dated window.
 * - `worked`: internal labour, the day the time was worked.
 * - `recorded`: a day recorded before day sources existed (an invoice day,
 *   or a typed receipt day). Kept, never re-certified as a service day.
 * - `historical_override`: the 8 October import overlay for an imported cost.
 * - `posted_day`: nothing better is known, so the day it was posted.
 */
export const costDateSources = [
  "corrected",
  "service",
  "used",
  "unknown",
  "worked",
  "recorded",
  "historical_override",
  "posted_day",
] as const;
export type CostDateSource = (typeof costDateSources)[number];

export const costDateSourceLabels: Record<CostDateSource, string> = {
  corrected: "Corrected day",
  service: "Service day",
  used: "Day used",
  unknown: "Day unknown",
  worked: "Day worked",
  recorded: "Recorded day",
  historical_override: "Historical day",
  posted_day: "Day posted",
};

/**
 * The date rule every money report and export states, in one place (TRBUD-458),
 * so a panel and its CSV cannot describe two different rules.
 */
export const COST_DAY_BASIS =
  "The day the work, service or part use happened, or a person's correction of it. " +
  "Costs recorded before day sources existed keep the day recorded then. " +
  "A cost whose day is unknown counts in no dated window.";

/** The same rule in one sentence, for the line under a report panel. */
export const COST_DAY_BASIS_SHORT =
  "Costs count on the day the work, service or part use happened where that is known; " +
  "older costs keep the day recorded when they were posted.";

/**
 * Whether a source names the day the thing actually happened, as opposed to a
 * fallback. The date-quality displays mark the fallbacks so a reader can see
 * how much of a figure rests on a posting or legacy day.
 */
export function isOccurrenceDay(source: CostDateSource): boolean {
  return source === "corrected" || source === "service" || source === "used" || source === "worked";
}

/**
 * The sources that are a FALLBACK: a day that stands in for the day the thing
 * happened. Unknown is not here; it has no day at all and is shown apart.
 */
export const FALLBACK_COST_DATE_SOURCES = ["recorded", "historical_override", "posted_day"] as const satisfies readonly CostDateSource[];

/**
 * The day and source a posting routine stores for a day a person reported.
 *
 * Three states, the same as the reviewed receipt day (20261027154856):
 * omitted keeps the old rule (no day, no source, so the posting day counts), an
 * explicit null records that the day is unknown, and a day records it as a
 * `service` or `used` day.
 */
export function reportedCostDay(
  value: string | null | undefined,
  basis: "service" | "used",
): { day: string | null; basis: "service" | "used" | "unknown" | null } {
  if (value === undefined) return { day: null, basis: null };
  if (value === null) return { day: null, basis: "unknown" };
  return { day: value, basis };
}

/**
 * `POST /work-orders/:id/costs/:costEntryId/correct-date`.
 *
 * `costDate` is REQUIRED and may be null: null says the day is not known, so the
 * cost leaves every dated window and shows with the unknown-day figures. There
 * is no amount here and there never will be: a date correction moves a cost
 * between periods and never changes what it cost.
 */
export const correctCostDateBody = strictObject({
  costDate: boundedDateString("recorded").nullable(),
  reason: z.string().trim().min(3).max(1000),
  /** Optional proof, such as the receipt showing the service day. Must be on this job. */
  evidenceDocumentId: z.string().uuid().nullish(),
  idempotencyKey,
});
export type CorrectCostDateBody = z.infer<typeof correctCostDateBody>;

export interface CorrectCostDateReceipt {
  correctionId: string;
  costEntryId: string;
  costDate: string | null;
  costDateSource: CostDateSource;
}

/** The newest correction of a cost, for the Costs card. */
export interface CostDateCorrectionSummary {
  previousCostDate: string | null;
  previousSource: CostDateSource;
  correctedByLabel: string | null;
  correctedAt: string;
  reason: string;
  /** How many corrections this cost has had in all. */
  count: number;
}

/* ────────────────────────────────────────────────────────────────────────
 * Sharing one visit's labour minutes across days
 * ──────────────────────────────────────────────────────────────────────── */

export interface DayDuration {
  /** Fleet-calendar day, `YYYY-MM-DD`. */
  day: string;
  /** Exact counted time on that day, in whole microseconds. */
  micros: number;
  /** The first counted instant on that day (ISO), which is what a time entry for the day records. */
  firstAt?: string;
}

const MICROS_PER_MINUTE = 60_000_000;

/**
 * Share one visit's billable minutes across the fleet days it touched.
 *
 * The labour policy rounds a visit UP to the whole minute ONCE (Eden,
 * 1 September 2026; `computeStageClock` and `tb_labour_minutes`). Rounding each
 * day on its own would add up to a minute per extra day, so instead:
 *
 * 1. The total is the policy's own figure: the summed exact time, rounded up.
 * 2. Each day gets its whole minutes, rounded down.
 * 3. The minutes left over (fewer than the number of days) go to the days with
 *    the largest leftover fraction; a tie goes to the earlier day.
 *
 * So the days always add up to exactly the visit's rounded total, no day gets
 * more than its own time rounded up, and the answer depends only on the time
 * worked, which keeps a retried stage move posting the same figures. Days that
 * end with no minutes are left out. The input order does not matter; the
 * output is in day order.
 */
export function shareMinutesByDay(durations: readonly DayDuration[]): {
  totalMinutes: number;
  days: Array<{ day: string; minutes: number }>;
} {
  const byDay = new Map<string, number>();
  for (const { day, micros } of durations) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) throw new Error(`not a calendar day: ${day}`);
    if (!Number.isSafeInteger(micros) || micros < 0) throw new Error(`not a whole, non-negative duration: ${micros}`);
    byDay.set(day, (byDay.get(day) ?? 0) + micros);
  }
  const days = [...byDay.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const totalMicros = days.reduce((sum, [, micros]) => sum + micros, 0);
  const totalMinutes = Math.ceil(totalMicros / MICROS_PER_MINUTE);
  const shares = days.map(([day, micros], order) => ({
    day,
    order,
    minutes: Math.floor(micros / MICROS_PER_MINUTE),
    leftover: micros % MICROS_PER_MINUTE,
  }));
  let remaining = totalMinutes - shares.reduce((sum, share) => sum + share.minutes, 0);
  for (const share of [...shares].sort((a, b) => b.leftover - a.leftover || a.order - b.order)) {
    if (remaining <= 0) break;
    if (share.leftover === 0) continue;
    share.minutes += 1;
    remaining -= 1;
  }
  return {
    totalMinutes,
    days: shares.filter((share) => share.minutes > 0).map(({ day, minutes }) => ({ day, minutes })),
  };
}
